import { dateUtils } from '@festina-life/core'
import { useResizeObserver } from '@festina-life/ui/hooks'
import { useMemo, useRef, useState } from 'react'

import { useConfigurationContext } from '../../context/configurationContext'
import { useSpecId } from '../../system/useSpecId'
import { getReconciliationSpecOperationFromDate } from '../../uiModels/reconciliationSpecOperations/helpers'
import { useApplicationViewOperationsContext } from '../../workspace/applicationViewOperationsContext'
import {
  ARROW_LINE_WIDTH,
  DATE_HEADING_HEIGHT,
} from '../versionedTimeline/constants'
import { getVersionDateBoundaries } from '../versionedTimeline/helpers'
import { TimelineScaleControls } from '../versionedTimeline/TimelineScaleControls'
import { VersionedArrowsGroup } from '../versionedTimeline/VersionedArrowsGroup'
import { ReconciliationSpecVersionsLine } from './ReconciliationSpecVersionsLine'

/**
 * Version history of the reconciliation spec in scope, used to pick the version to view.
 */
export const ReconciliationSpecTimelineDiagram = () => {
  const divRef = useRef<HTMLDivElement>(null)
  const divObserver = useResizeObserver(divRef)
  const { viewAt } = useConfigurationContext()
  const specId = useSpecId()
  const { applicationReconciliationSpecs } =
    useApplicationViewOperationsContext()
  const indexedReconciliationSpecOperations =
    applicationReconciliationSpecs.get(specId)
  const [currentScale, setCurrentScale] = useState(1)

  const allVersions = useMemo(
    () => indexedReconciliationSpecOperations?.operations ?? [],
    [indexedReconciliationSpecOperations?.operations],
  )

  const { earliestDate: earliestVersionDate, latestDate: latestVersionDate } =
    useMemo(
      () =>
        getVersionDateBoundaries({
          items: allVersions,
          getFromDate: (version) =>
            getReconciliationSpecOperationFromDate(version.internalOperation),
          defaultDate: dateUtils.dateAsServerStr(viewAt),
        }),
      [allVersions, viewAt],
    )

  return (
    <div ref={divRef}>
      <div className="group relative grid grid-cols-[auto_1fr] overflow-hidden">
        <TimelineScaleControls step={0.5} onChange={setCurrentScale} />
        <div className="overflow-auto">
          <VersionedArrowsGroup
            height={DATE_HEADING_HEIGHT + ARROW_LINE_WIDTH}
            scale={currentScale}
            parentWidth={divObserver?.width ?? 0}
            earliestVersionDate={earliestVersionDate}
            latestVersionDate={latestVersionDate}
            nodeHeight={0} // single node, so height isn't needed for positioning
            versions={allVersions}
            paddingTop={0}
          >
            {ReconciliationSpecVersionsLine}
          </VersionedArrowsGroup>
        </div>
      </div>
    </div>
  )
}
